import { cacheExchange, createClient, fetchExchange } from '@urql/core';
import { persistedExchange } from '@urql/exchange-persisted';

export const createServerClient = () =>
  createClient({
    url: process.env.GQL_GATEWAY || `${process.env.SERVER_HOST}/api/gateway/`,
    exchanges: [cacheExchange, fetchExchange],
  });

// persistedExchange's default hasher silently falls back to '' in bundled server runtimes.
export const sha256 = async (query: string) => {
  const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(query));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
};

/**
 * Long enough for a gateway miss that resolves in the configcenter, short enough that a hung
 * gateway delays a render by this much at most before the caller's fallback renders. A request
 * aborted by this timeout still fills the gateway's response cache for the next one, because the
 * gateway runs without Yoga's `useExecutionCancellation`.
 */
export const BACKOFFICE_TIMEOUT_MS = 3000;

const PAUSE_AFTER_FAILURE_MS = 10_000;

// Module state, so a pause holds across requests: during an outage the process sends one
// request per pause instead of one per render. 0 while the backoffice answers.
let pausedUntil = 0;

const pauseRequests = (reason: unknown, isFirstAfterPause: boolean) => {
  const now = Date.now();
  // Requests that were already running when the pause began add no line of their own.
  if (isFirstAfterPause || now >= pausedUntil) {
    const cause = reason instanceof Error && reason.cause ? ` (${String(reason.cause)})` : '';
    // eslint-disable-next-line no-console -- gql is a leaf package and cannot import the shared logger
    console.warn(
      `Backoffice request failed: ${String(reason)}${cause}. Pausing backoffice requests for ${PAUSE_AFTER_FAILURE_MS / 1000} s.`,
    );
  }
  pausedUntil = now + PAUSE_AFTER_FAILURE_MS;
};

// urql overwrites `fetchOptions.signal` with its own AbortController, so the timeout wraps
// `fetch` instead. Both signals live for one request only; nothing long-lived holds a listener.
const withTimeout = (init: RequestInit | undefined, timeoutMs: number): RequestInit => ({
  ...init,
  signal: init?.signal
    ? AbortSignal.any([init.signal, AbortSignal.timeout(timeoutMs)])
    : AbortSignal.timeout(timeoutMs),
});

const backofficeFetch =
  (timeoutMs: number, controlsPause: boolean): typeof fetch =>
  async (input, init) => {
    const now = Date.now();
    if (now < pausedUntil) {
      throw new Error('Backoffice requests are paused after a failed request');
    }
    // A client with a longer timeout would hold the first request after a pause for that long, and
    // its late failures would outlive the pause and start a new one.
    if (!controlsPause) return fetch(input, withTimeout(init, timeoutMs));

    // The first request after a pause tries alone; the others keep failing until it has an answer.
    const isFirstAfterPause = pausedUntil !== 0;
    if (isFirstAfterPause) pausedUntil = now + timeoutMs;

    try {
      const response = await fetch(input, withTimeout(init, timeoutMs));
      if (response.status >= 500) pauseRequests(`HTTP ${response.status}`, isFirstAfterPause);
      else if (isFirstAfterPause) pausedUntil = 0;
      return response;
    } catch (error) {
      // urql aborts its own signal when it drops an operation; that is no backoffice failure.
      if (!init?.signal?.aborted) pauseRequests(error, isFirstAfterPause);
      throw error;
    }
  };

// APQ only behind the gateway (BACKOFFICE_GQL_APQ=true on master/prod). The configcenter
// answers a hash-only request with 400 instead of PersistedQueryNotFound, so the
// BACKOFFICE_HOST fallback must never get hashes.
export const createBackofficeServerClient = ({
  persistedQueries = true,
  timeoutMs = BACKOFFICE_TIMEOUT_MS,
  controlsPause = true,
}: {
  persistedQueries?: boolean;
  timeoutMs?: number;
  /** false: the client's reads fail during a pause, but never start, extend or end one. */
  controlsPause?: boolean;
} = {}) => {
  const sendHashes =
    persistedQueries &&
    !!process.env.BACKOFFICE_GQL_GATEWAY &&
    process.env.BACKOFFICE_GQL_APQ === 'true';

  return createClient({
    url: process.env.BACKOFFICE_GQL_GATEWAY || `${process.env.BACKOFFICE_HOST}/api/graphql`,
    // A local backoffice under `next dev` compiles `/api/graphql` on its first request, which
    // outlasts any sensible timeout and would pause every read after it.
    fetch:
      process.env.NODE_ENV === 'development'
        ? undefined
        : backofficeFetch(timeoutMs, controlsPause),
    exchanges: [
      cacheExchange,
      ...(sendHashes ? [persistedExchange({ generateHash: sha256 })] : []),
      fetchExchange,
    ],
  });
};
